import { useMemo } from 'react'

import { ALL_SUBCOLLECTION_FILTER_ID } from '@src/pages/[...slug]/components/collection/collection.constants'
import { scrollToFiltersIfSticky } from '@src/pages/[...slug]/components/collection/collection.helpers'
import { UpdateQueryParams } from '@src/quince-components/src/atoms/filterSection/menu/filterSection.interface'

import type { useCLPFilters } from './use-clp-filters'

const ALL_SUBCOLLECTION_IDS = [ALL_SUBCOLLECTION_FILTER_ID, 'All', 'all'] as const
const DEFAULT_SUBCOLLECTION_ID = 'subcollections'

function isAllSubcollection(id: string): boolean {
  return ALL_SUBCOLLECTION_IDS.some(val => val.toLowerCase() === id.toLowerCase())
}

type FilterMethods = Pick<
  ReturnType<typeof useCLPFilters>,
  'updateFilter' | 'updateSort' | 'clearAllFilters' | 'setOnlyFilter' | 'setFilters'
>

interface UseFilterUpdateQueryParams extends FilterMethods {
  subcollectionId?: string
  isDesktop: boolean
  onSelectSubcollection?: (subcollectionId: string) => void
  onResetSubcollection?: () => void
  resolveSubcollectionSelection?: (filters: Record<string, string[]>) => string | null | undefined
  /** Lets the toggle case detect "already-selected" and clear instead of re-setting. */
  selectedFilters?: Record<string, string[]>
}

/**
 * Builds the `updateQuery` adapter used by filter/sort UI components.
 * Handles subcollection toggle semantics (selecting via dropdown clears other filters)
 * and scroll-to-filter behaviour.
 */
export function useFilterUpdateQuery({
  updateFilter,
  updateSort,
  clearAllFilters,
  setOnlyFilter,
  setFilters,
  subcollectionId = DEFAULT_SUBCOLLECTION_ID,
  isDesktop,
  onSelectSubcollection,
  onResetSubcollection,
  resolveSubcollectionSelection,
  selectedFilters,
}: UseFilterUpdateQueryParams): (params: UpdateQueryParams) => void {
  return useMemo(
    () => (params: UpdateQueryParams) => {
      const { filter, sort } = params

      if (sort?.type === 'toggle') {
        scrollToFiltersIfSticky(isDesktop)
        updateSort(sort.value)
        return
      }

      if (!filter) return

      switch (filter.type) {
        case 'overwrite': {
          const newFilters: Record<string, string[]> = {}
          for (const [key, values] of Object.entries(filter.updatedFilters)) {
            if (Array.isArray(values) && values.length > 0) {
              newFilters[key] = values
            }
          }

          const resolvedSubcollectionSelection = resolveSubcollectionSelection?.(newFilters)
          if (resolvedSubcollectionSelection !== undefined) {
            if (resolvedSubcollectionSelection === null || isAllSubcollection(resolvedSubcollectionSelection)) {
              if (onResetSubcollection) {
                onResetSubcollection()
                return
              }
            } else {
              if (onSelectSubcollection) {
                onSelectSubcollection(resolvedSubcollectionSelection)
                return
              }
            }
          }

          scrollToFiltersIfSticky(isDesktop)
          setFilters(newFilters)
          break
        }
        case 'toggle':
          if (filter.name && filter.value) {
            if (filter.name === subcollectionId) {
              // Path mode: only take the navigation branch when the matching callback is wired;
              // partial wiring falls through to legacy filter mutation instead of silently no-op.
              if (isAllSubcollection(filter.value) && onResetSubcollection) {
                onResetSubcollection()
                return
              }
              if (!isAllSubcollection(filter.value) && onSelectSubcollection) {
                onSelectSubcollection(filter.value)
                return
              }
              if (isAllSubcollection(filter.value)) {
                clearAllFilters({ trackFiltersCleared: false })
                return
              }
              // Query mode: clicking the currently-selected value clears, else replace with it.
              const currentSubcollectionValues = selectedFilters?.[filter.name] ?? []
              const isAlreadySelected = currentSubcollectionValues.includes(filter.value)
              if (isAlreadySelected) {
                clearAllFilters({ trackFiltersCleared: false })
              } else {
                setOnlyFilter(filter.name, [filter.value])
              }
              return
            }
            scrollToFiltersIfSticky(isDesktop)
            updateFilter({ filterId: filter.name, values: [filter.value], mode: 'toggle' })
          }
          break
        case 'remove':
          if (filter.name && filter.value) {
            if (filter.name === subcollectionId && onResetSubcollection) {
              onResetSubcollection()
              return
            }
            scrollToFiltersIfSticky(isDesktop)
            updateFilter({ filterId: filter.name, values: [filter.value], mode: 'remove' })
          }
          break
        case 'removeAll':
          if (filter.name) {
            if (filter.name === subcollectionId && onResetSubcollection) {
              onResetSubcollection()
              return
            }
            scrollToFiltersIfSticky(isDesktop)
            updateFilter({ filterId: filter.name, values: [], mode: 'replace' })
          }
          break
        case 'reset':
          if (onResetSubcollection) {
            onResetSubcollection()
          } else {
            scrollToFiltersIfSticky(isDesktop)
            clearAllFilters()
          }
          break
      }
    },
    [
      updateSort,
      updateFilter,
      setFilters,
      clearAllFilters,
      setOnlyFilter,
      subcollectionId,
      isDesktop,
      onResetSubcollection,
      onSelectSubcollection,
      resolveSubcollectionSelection,
      selectedFilters,
    ],
  )
}
